import React, { useState, useEffect } from "react";
import {
    UserPlus,
    Mail,
    Lock,
    User,
    Shield,
    CheckCircle,
    AlertCircle,
    Loader2,
    ChevronRight,
    Search,
    Filter,
    X,
    Key,
    UserX,
    UserCheck,
    RefreshCw,
    Building2,
    MapPin,
    CreditCard,
    Trash2,
    Edit3,
    Command,
    ToggleLeft,
    ToggleRight,
    Eye,
    Truck,
    Package,
    ShoppingBag,
    Boxes,
    UserCog,
    PlayCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_BASE_URL } from "../../utils/api";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { collection, query, where, getDocs, doc, getDoc, updateDoc, orderBy } from "firebase/firestore";
import { useImpersonation } from "../../context/ImpersonationContext";

export const USER_PERMISSIONS_LIST = [
    { id: "shipments_view", label: "View Shipments" },
    { id: "shipments_create", label: "Create Shipments" },
    { id: "shipments_edit", label: "Edit Shipments" },
    { id: "customs_view", label: "View Customs Entries" },
    { id: "customs_create", label: "Create Customs Entries" },
    { id: "reports_view", label: "Access Reports" },
    { id: "inventory_view", label: "Access Inventory" },
    { id: "documents_view", label: "Access Documents" },
    { id: "chat_access", label: "Access Chat" },
    { id: "tms_view", label: "Access TMS" }
];

export default function UserManagement() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        role: "client",
        companyId: "",
        paymentPlan: "starter",
        permissionTemplate: "manager"
    });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: null, message: "" });
    const [users, setUsers] = useState([]);
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [fetchError, setFetchError] = useState("");

    const location = useLocation();
    const navigate = useNavigate();
    const { startImpersonation } = useImpersonation();
    const queryParams = new URLSearchParams(location.search);
    const tenantFromUrl = queryParams.get("tenant") || "";
    const [searchTerm, setSearchTerm] = useState(tenantFromUrl);

    // Companies for tenant dropdown
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        getDocs(query(collection(db, "companies"), orderBy("name")))
            .then(snap => {
                const list = [];
                snap.forEach(d => {
                    const data = d.data();
                    if (data.status !== "deleted") list.push({ id: d.id, name: data.name || d.id });
                });
                setCompanies(list);
            })
            .catch(() => {});
    }, []);

    // Pre-fill companyId in create form when coming from Tenant Management
    useEffect(() => {
        if (tenantFromUrl) {
            setFormData(prev => ({ ...prev, companyId: tenantFromUrl }));
        }
    }, [tenantFromUrl]);

    const displayedUsers = (Array.isArray(users) ? users : []).filter((user) => {
        if (!searchTerm) return true;
        const lowSearch = searchTerm.toLowerCase();
        return (
            (user.name || "").toLowerCase().includes(lowSearch) ||
            (user.email || "").toLowerCase().includes(lowSearch) ||
            (user.companyId || "").toLowerCase().includes(lowSearch)
        );
    });

    // Manage User Modal State
    const [managingUser, setManagingUser] = useState(null);
    const [manageTab, setManageTab] = useState("details"); // 'details', 'status', 'password', 'delete', 'permissions'
    const [newPassword, setNewPassword] = useState("");
    const [editDetailsData, setEditDetailsData] = useState({ name: "", companyId: "", address: "", paymentPlan: "" });
    const [tempPermissions, setTempPermissions] = useState([]);
    const [manageLoading, setManageLoading] = useState(false);
    const [manageStatus, setManageStatus] = useState({ type: null, message: "" });
    const [tempModules, setTempModules] = useState([]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                fetchUsers(user);
            } else {
                setFetchingUsers(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const fetchUsers = async (currentUser) => {
        setFetchingUsers(true);
        setFetchError("");
        try {
            const idToken = await currentUser.getIdToken();
            const apiUrl = API_BASE_URL;
            const requestUrl = `${apiUrl}/api/admin/users`;

            console.log("📡 [UserManagement] Fetching users from:", requestUrl);

            const response = await axios.get(requestUrl, {
                headers: {
                    Authorization: `Bearer ${idToken}`
                }
            });

            console.log("🟢 [UserManagement] API response for users:", response.data);

            if (Array.isArray(response.data)) {
                setUsers(response.data);
            } else {
                const isHtml = typeof response.data === 'string' && response.data.trim().startsWith('<!doctype html');
                console.error("❌ Unexpected API response format:", typeof response.data);

                setUsers([]);
                if (isHtml) {
                    setFetchError("API request is hitting the frontend server instead of the backend. Set REACT_APP_API_URL=https://api.tinyglobalop.com in your Vercel environment variables, then redeploy.");
                } else {
                    setFetchError(`Invalid user data format (expected Array, got ${typeof response.data}). Please check server logs.`);
                }
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            if (err.response && err.response.data && err.response.data.error) {
                setFetchError(err.response.data.error);
                setStatus({ type: "error", message: err.response.data.error });
            } else {
                setFetchError("Failed connecting to the backend server.");
            }
        } finally {
            setFetchingUsers(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus({ type: null, message: "" });

        try {
            // Get the current admin's ID token for authentication
            const idToken = await auth.currentUser.getIdToken();

            // PLAN LIMIT ENFORCEMENT
            if (formData.companyId) {
                const subsQ = query(collection(db, "subscriptions"), where("tenantId", "==", formData.companyId), where("status", "==", "active"));
                const subsSnap = await getDocs(subsQ);
                if (!subsSnap.empty) {
                    const activeSub = subsSnap.docs[0].data();
                    const planSnap = await getDoc(doc(db, "plans", activeSub.planId));
                    if (planSnap.exists()) {
                        const planLimit = planSnap.data().userLimit;
                        if (planLimit !== -1) {
                            // Find current users for this tenant
                            const currentTenantUsers = (Array.isArray(users) ? users : []).filter(u => u.companyId === formData.companyId);
                            if (currentTenantUsers.length >= planLimit) {
                                setStatus({ type: "error", message: `Limit Reached: This tenant's plan (${planSnap.data().name}) restricts them to ${planLimit} users. Please upgrade their plan.` });
                                setLoading(false);
                                return;
                            }
                        }
                    }
                }
            }

            const apiUrl = API_BASE_URL;
            const response = await axios.post(`${apiUrl}/api/admin/create-user`, formData, {
                headers: {
                    Authorization: `Bearer ${idToken}`
                }
            });

            setStatus({
                type: "success",
                message: `User ${response.data.email} created successfully!`
            });
            setFormData({ name: "", email: "", password: "", role: "client", companyId: "", paymentPlan: "starter", permissionTemplate: "manager" });
            fetchUsers(auth.currentUser); // Refresh the list

        } catch (err) {
            console.error("Error creating user:", err);
            setStatus({
                type: "error",
                message: err.response?.data?.error || "Failed to create user. Please try again."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (uid, newStatus) => {
        setManageLoading(true);
        setManageStatus({ type: null, message: "" });
        try {
            const idToken = await auth.currentUser.getIdToken();
            const apiUrl = API_BASE_URL;
            await axios.put(`${apiUrl}/api/admin/users/${uid}/status`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setManageStatus({ type: "success", message: `User status changed to ${newStatus}.` });
            fetchUsers(auth.currentUser);
            if (managingUser) setManagingUser({ ...managingUser, status: newStatus });
        } catch (err) {
            console.error("Error updating status:", err);
            setManageStatus({ type: "error", message: err.response?.data?.error || "Failed to update status." });
        } finally {
            setManageLoading(false);
        }
    };

    const handleUpdatePassword = async (uid) => {
        if (!newPassword || newPassword.length < 6) {
            setManageStatus({ type: "error", message: "Password must be at least 6 characters." });
            return;
        }
        setManageLoading(true);
        setManageStatus({ type: null, message: "" });
        try {
            const idToken = await auth.currentUser.getIdToken();
            const apiUrl = API_BASE_URL;
            await axios.put(`${apiUrl}/api/admin/users/${uid}/password`, { password: newPassword }, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setManageStatus({ type: "success", message: "Password updated successfully!" });
            setNewPassword("");
        } catch (err) {
            console.error("Error updating password:", err);
            setManageStatus({ type: "error", message: err.response?.data?.error || "Failed to update password." });
        } finally {
            setManageLoading(false);
        }
    };

    const handleUpdateDetails = async (uid) => {
        setManageLoading(true);
        setManageStatus({ type: null, message: "" });
        try {
            const idToken = await auth.currentUser.getIdToken();
            const apiUrl = API_BASE_URL;
            await axios.put(`${apiUrl}/api/admin/users/${uid}/details`, editDetailsData, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setManageStatus({ type: "success", message: "User details updated successfully!" });
            fetchUsers(auth.currentUser);
            if (managingUser) setManagingUser({ ...managingUser, ...editDetailsData });
        } catch (err) {
            console.error("Error updating details:", err);
            setManageStatus({ type: "error", message: err.response?.data?.error || "Failed to update details." });
        } finally {
            setManageLoading(false);
        }
    };

    const handleUpdatePermissions = async (uid) => {
        setManageLoading(true);
        setManageStatus({ type: null, message: "" });
        try {
            await updateDoc(doc(db, "users", uid), { userPermissions: tempPermissions });
            setManageStatus({ type: "success", message: "User permissions updated successfully!" });
            fetchUsers(auth.currentUser);
            if (managingUser) setManagingUser({ ...managingUser, userPermissions: tempPermissions });
        } catch (err) {
            console.error("Error updating permissions:", err);
            setManageStatus({ type: "error", message: "Failed to update permissions." });
        } finally {
            setManageLoading(false);
        }
    };

    const handleSaveModules = async (uid) => {
        setManageLoading(true);
        setManageStatus({ type: null, message: "" });
        try {
            const idToken = await auth.currentUser.getIdToken();
            const apiUrl = API_BASE_URL;
            await axios.put(`${apiUrl}/api/admin/users/${uid}/details`, { enabledModules: tempModules }, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setManageStatus({ type: "success", message: "Modules updated successfully." });
            fetchUsers(auth.currentUser);
        } catch (err) {
            setManageStatus({ type: "error", message: err.response?.data?.error || "Failed to update modules." });
        } finally {
            setManageLoading(false);
        }
    };

    const handleDeleteUser = async (uid) => {
        if (!window.confirm("Are you sure you want to completely delete this user? This cannot be undone.")) return;
        setManageLoading(true);
        setManageStatus({ type: null, message: "" });
        try {
            const idToken = await auth.currentUser.getIdToken();
            const apiUrl = API_BASE_URL;
            await axios.delete(`${apiUrl}/api/admin/users/${uid}`, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            setManagingUser(null);
            fetchUsers(auth.currentUser);
        } catch (err) {
            console.error("Error deleting user:", err);
            setManageStatus({ type: "error", message: err.response?.data?.error || "Failed to delete user." });
        } finally {
            setManageLoading(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto py-8 px-4">
            {tenantFromUrl && (
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-900 mb-4 transition-colors font-semibold"
                >
                    ← Back to Tenant Management
                    {companies.find(c => c.id === tenantFromUrl) && (
                        <span className="ml-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-[10px] font-bold">
                            {companies.find(c => c.id === tenantFromUrl)?.name}
                        </span>
                    )}
                </button>
            )}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">User Management</h1>
                    <p className="text-slate-500 mt-1">
                        {tenantFromUrl && companies.find(c => c.id === tenantFromUrl)
                            ? <>Viewing users for <strong>{companies.find(c => c.id === tenantFromUrl)?.name}</strong></>
                            : "Provision and manage platform access for Clients and Agents."
                        }
                    </p>
                </div>
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100 flex items-center gap-2">
                    <Shield size={18} className="text-blue-600" />
                    <span className="text-sm font-bold text-blue-700 uppercase tracking-wider">Admin Secure Sub-system</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* User Creation Form */}
                <div className="lg:col-span-1">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden"
                    >
                        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                            <h3 className="font-bold text-navy-900 flex items-center gap-2">
                                <UserPlus size={20} className="text-navy-900" /> Create New Account
                            </h3>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Full Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter full name"
                                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="user@example.com"
                                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Company / Tenant</label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                                        <select
                                            name="companyId"
                                            value={formData.companyId}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 transition-all appearance-none"
                                        >
                                            <option value="">— No company (ops/agent) —</option>
                                            {companies.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Payment Plan</label>
                                    <div className="relative">
                                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                        <select
                                            name="paymentPlan"
                                            value={formData.paymentPlan}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                                        >
                                            <option value="starter">Starter</option>
                                            <option value="growth">Growth</option>
                                            <option value="enterprise">Enterprise</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Temporary Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input
                                        type="password"
                                        name="password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Role Template (Permissions)</label>
                                <div className="relative">
                                    <Command className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <select
                                        name="permissionTemplate"
                                        value={formData.permissionTemplate}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 transition-all"
                                    >
                                        <option value="admin">Admin (Full Access)</option>
                                        <option value="manager">Manager (Ops + Reports)</option>
                                        <option value="operator">Operator (Execution Only)</option>
                                        <option value="viewer">Viewer (Read Only)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Base Platform Role</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { role: "client",   label: "Client",   icon: <User size={16}/>,       desc: "Manages shipments & orders" },
                                        { role: "agent",    label: "Agent",    icon: <Shield size={16}/>,     desc: "Customs & compliance" },
                                        { role: "operator", label: "Operator", icon: <UserCog size={16}/>,    desc: "Processes requests" },
                                        { role: "3pl",      label: "3PL",      icon: <Boxes size={16}/>,      desc: "Warehouse partner" },
                                        { role: "vendor",   label: "Vendor",   icon: <Package size={16}/>,    desc: "Supplies goods, sends ASNs" },
                                        { role: "admin",    label: "Admin",    icon: <Building2 size={16}/>,  desc: "Company admin" },
                                    ].map(({ role, label, icon, desc }) => (
                                        <button
                                            key={role}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, role })}
                                            className={`py-2.5 px-2 rounded-xl border text-xs font-bold transition-all flex flex-col items-center gap-1 ${formData.role === role
                                                ? "bg-navy-900 text-white border-navy-900 shadow-lg shadow-navy-900/20"
                                                : "bg-white text-slate-500 border-slate-200 hover:border-navy-900/30"
                                                }`}
                                        >
                                            {icon}
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1.5">
                                    {{
                                        client: "Manages shipments, inventory & orders",
                                        agent: "Customs entries & compliance review",
                                        operator: "Processes client requests",
                                        "3pl": "Warehouse management & inbound receiving",
                                        vendor: "Supplies goods & creates inbound ASNs",
                                        admin: "Full company admin access",
                                    }[formData.role] || ""}
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-4 bg-navy-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 disabled:bg-slate-300 disabled:shadow-none"
                            >
                                {loading ? (
                                    <Loader2 className="animate-spin" size={20} />
                                ) : (
                                    <>Create User Now <ChevronRight size={18} /></>
                                )}
                            </button>

                            <AnimatePresence>
                                {status.type && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className={`p-4 rounded-xl flex items-start gap-3 ${status.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-red-50 text-red-700 border border-red-100"
                                            }`}
                                    >
                                        {status.type === "success" ? <CheckCircle size={18} className="shrink-0" /> : <AlertCircle size={18} className="shrink-0" />}
                                        <p className="text-xs font-bold">{status.message}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </form>
                    </motion.div>
                </div>

                {/* Directory Preview / Stats */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Quick Role Preview Panel */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <PlayCircle size={16} className="text-teal-600"/>
                            <h3 className="font-bold text-navy-900 text-sm">Quick Role Preview</h3>
                            <span className="text-xs text-slate-400 ml-1">— Test any dashboard instantly</span>
                        </div>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                            {[
                                { role: "client",      label: "Client",    path: "/client",      color: "bg-blue-50 text-blue-700 hover:bg-blue-100",     icon: <User size={14}/> },
                                { role: "agent",       label: "Agent",     path: "/agent",       color: "bg-teal-50 text-teal-700 hover:bg-teal-100",     icon: <Shield size={14}/> },
                                { role: "operator",    label: "Operator",  path: "/operator",    color: "bg-orange-50 text-orange-700 hover:bg-orange-100",icon: <UserCog size={14}/> },
                                { role: "3pl",         label: "3PL",       path: "/3pl",         color: "bg-yellow-50 text-yellow-700 hover:bg-yellow-100",icon: <Boxes size={14}/> },
                                { role: "vendor",      label: "Vendor",    path: "/vendor",      color: "bg-violet-50 text-violet-700 hover:bg-violet-100",icon: <Package size={14}/> },
                                { role: "admin",       label: "Admin",     path: "/admin",       color: "bg-purple-50 text-purple-700 hover:bg-purple-100",icon: <Building2 size={14}/> },
                            ].map(({ role, label, path, color, icon }) => (
                                <button
                                    key={role}
                                    onClick={async () => {
                                        await startImpersonation({
                                            uid: auth.currentUser?.uid,
                                            name: `Test ${label}`,
                                            email: `test-${role}@tinyglobal.com`,
                                            role,
                                            companyId: "demo",
                                            userPermissions: [],
                                            features: {},
                                            enabledModules: null,
                                        });
                                        navigate(path);
                                    }}
                                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl text-xs font-bold transition-colors ${color}`}
                                >
                                    {icon}
                                    {label}
                                </button>
                            ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-3">Uses demo company data. A banner will appear — click "Exit Testing Mode" to return.</p>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            <h3 className="font-bold text-navy-900">Platform Accounts</h3>
                            <div className="flex gap-2 items-center">
                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, email, or tenant..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-navy-900 transition-all w-full md:w-64"
                                    />
                                    {searchTerm && (
                                        <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                <Filter size={18} className="text-slate-400 cursor-pointer hidden md:block" />
                            </div>
                        </div>
                        <div className="p-0">
                            {/* Desktop Table */}
                            <table className="w-full text-left hidden md:table">
                                <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                                    <tr>
                                        <th className="px-6 py-4">User</th>
                                        <th className="px-6 py-4">Tenant</th>
                                        <th className="px-6 py-4">Role</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {fetchingUsers ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-slate-500">
                                                <Loader2 className="animate-spin inline-block text-navy-900" size={24} />
                                            </td>
                                        </tr>
                                    ) : fetchError ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-red-500 font-bold bg-red-50">
                                                {fetchError}
                                            </td>
                                        </tr>
                                    ) : displayedUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-slate-500">
                                                No users found.
                                            </td>
                                        </tr>
                                    ) : (
                                        displayedUsers.map((user) => (
                                            <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-navy-900 text-white flex items-center justify-center font-bold text-xs uppercase">
                                                            {user.name ? user.name.charAt(0) : user.email?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-bold text-navy-900">{user.name || "N/A"}</div>
                                                            <div className="text-[10px] text-slate-400">{user.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {user.companyId ? (
                                                        <button
                                                            onClick={() => navigate(`${location.pathname.startsWith("/super-admin") ? "/super-admin" : "/admin"}/tenants`)}
                                                            className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                                            title={user.companyId}
                                                        >
                                                            {companies.find(c => c.id === user.companyId)?.name || user.companyId.slice(0, 12) + "…"}
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-xs font-bold text-navy-700 uppercase tracking-widest">{user.role}</td>
                                                <td className="px-6 py-4">
                                                    {(user.status === "disabled") ? (
                                                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-[10px] font-bold">DISABLED</span>
                                                    ) : (
                                                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">ACTIVE</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-3">
                                                        <button
                                                                onClick={async () => {
                                                                    await startImpersonation(user);
                                                                    const roleRoutes = {
                                                                        "super-admin": "/super-admin",
                                                                        admin: "/admin",
                                                                        client: "/client",
                                                                        operator: "/operator",
                                                                        agent: "/agent",
                                                                        "3pl": "/3pl",
                                                                        customer: "/client",
                                                                        vendor: "/vendor",
                                                                    };
                                                                    navigate(roleRoutes[user.role] || "/client");
                                                                }}
                                                                title="Preview this user's dashboard"
                                                                className="flex items-center gap-1 text-xs font-bold text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-2 py-1 rounded-lg transition-colors"
                                                            >
                                                                <Eye size={13} />
                                                                Test
                                                            </button>
                                                        <button
                                                            onClick={() => {
                                                                setManagingUser(user);
                                                                setManageTab("details");
                                                                setManageStatus({ type: null, message: "" });
                                                                setNewPassword("");
                                                                setEditDetailsData({
                                                                    name: user.name || "",
                                                                    companyId: user.companyId || "",
                                                                    address: user.address || "",
                                                                    paymentPlan: user.paymentPlan || ""
                                                                });
                                                                setTempModules(user.enabledModules || []);
                                                            }}
                                                            className="text-slate-400 hover:text-navy-900 transition-colors text-sm"
                                                        >
                                                            Manage
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                            {/* Mobile Cards */}
                            <div className="block md:hidden p-4 space-y-4">
                                {fetchingUsers ? (
                                    <div className="py-8 text-center text-slate-500">
                                        <Loader2 className="animate-spin inline-block text-navy-900" size={24} />
                                    </div>
                                ) : fetchError ? (
                                    <div className="p-4 text-center text-sm text-red-500 font-bold bg-red-50 rounded-xl">
                                        {fetchError}
                                    </div>
                                ) : displayedUsers.length === 0 ? (
                                    <div className="py-8 text-center text-slate-500 text-sm">
                                        No users found.
                                    </div>
                                ) : (
                                    displayedUsers.map((user) => (
                                        <div key={user.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-3">
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-navy-900 text-white flex items-center justify-center font-bold text-sm shadow-md uppercase shrink-0">
                                                        {user.name ? user.name.charAt(0) : user.email?.charAt(0) || '?'}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-navy-900 text-sm truncate max-w-[170px]">{user.name || "N/A"}</div>
                                                        <div className="text-xs text-slate-400 truncate max-w-[170px]">{user.email}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                    <span className="text-slate-400 font-medium block">Tenant</span>
                                                    <span className="font-semibold text-blue-600">
                                                        {user.companyId
                                                            ? (companies.find(c => c.id === user.companyId)?.name || user.companyId.slice(0, 10) + "…")
                                                            : "—"}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 font-medium block">Role</span>
                                                    <span className="font-bold text-navy-700 uppercase">{user.role}</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 font-medium block">Status</span>
                                                    {(user.status === "disabled") ? (
                                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold">DISABLED</span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">ACTIVE</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="pt-3 mt-1 border-t border-slate-50">
                                                <button
                                                    onClick={() => {
                                                        setManagingUser(user);
                                                        setManageTab("details");
                                                        setManageStatus({ type: null, message: "" });
                                                        setNewPassword("");
                                                        setEditDetailsData({
                                                            name: user.name || "",
                                                            companyId: user.companyId || "",
                                                            address: user.address || "",
                                                            paymentPlan: user.paymentPlan || ""
                                                        });
                                                        setTempPermissions(user.userPermissions || []);
                                                        setTempModules(user.enabledModules || []);
                                                    }}
                                                    className="w-full py-2 bg-slate-50 text-slate-600 font-bold rounded-lg text-sm hover:bg-slate-100 hover:text-navy-900 transition-colors"
                                                >
                                                    Manage User
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-navy-900 rounded-2xl p-8 text-white shadow-xl shadow-navy-900/30 relative overflow-hidden">
                        <div className="relative z-10">
                            <h4 className="text-xl font-bold mb-2 font-serif">Platform Integrity Notice</h4>
                            <p className="text-navy-300 text-sm leading-relaxed mb-6">
                                Creating users triggers standard security protocols. New Agents will have full access to Customs Entries and Compliance modules. New Clients will be able to manage their own shipments and inventory.
                            </p>
                            <div className="flex gap-4">
                                <div className="bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                                    <div className="text-xs text-navy-400 font-bold uppercase tracking-widest">New Clients</div>
                                    <div className="text-2xl font-bold text-emerald-400">+14 This Month</div>
                                </div>
                                <div className="bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                                    <div className="text-xs text-navy-400 font-bold uppercase tracking-widest">New Agents</div>
                                    <div className="text-2xl font-bold text-blue-400">+3 This Month</div>
                                </div>
                            </div>
                        </div>
                        {/* Abstract background element */}
                        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                    </div>
                </div>
            </div>

            {/* Manage User Modal */}
            <AnimatePresence>
                {managingUser && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ y: 50, opacity: 0, scale: 0.95 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 20, opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 relative">
                                <div>
                                    <h3 className="font-bold text-navy-900 text-xl">Manage User</h3>
                                    <p className="text-sm text-slate-500">{managingUser.email}</p>
                                </div>
                                <button
                                    onClick={() => setManagingUser(null)}
                                    className="p-2 text-slate-400 hover:bg-slate-200 hover:text-navy-900 rounded-full transition-colors absolute top-4 right-4"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="flex border-b border-slate-100 overflow-x-auto whitespace-nowrap scrollbar-hide">
                                <button
                                    onClick={() => setManageTab("details")}
                                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${manageTab === "details" ? "text-navy-900 border-b-2 border-navy-900 bg-navy-50/50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                                        }`}
                                >
                                    Details
                                </button>
                                <button
                                    onClick={() => setManageTab("status")}
                                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${manageTab === "status" ? "text-navy-900 border-b-2 border-navy-900 bg-navy-50/50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                                        }`}
                                >
                                    Status
                                </button>
                                <button
                                    onClick={() => setManageTab("permissions")}
                                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${manageTab === "permissions" ? "text-navy-900 border-b-2 border-navy-900 bg-navy-50/50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                                        }`}
                                >
                                    Permissions
                                </button>
                                {managingUser?.role === "client" && (
                                    <button
                                        onClick={() => setManageTab("modules")}
                                        className={`px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${manageTab === "modules" ? "text-navy-900 border-b-2 border-navy-900 bg-navy-50/50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                                            }`}
                                    >
                                        Modules
                                    </button>
                                )}
                                <button
                                    onClick={() => setManageTab("password")}
                                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${manageTab === "password" ? "text-navy-900 border-b-2 border-navy-900 bg-navy-50/50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                                        }`}
                                >
                                    Password
                                </button>
                                <button
                                    onClick={() => setManageTab("delete")}
                                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${manageTab === "delete" ? "text-red-600 border-b-2 border-red-600 bg-red-50/50" : "text-slate-400 hover:text-red-500 hover:bg-slate-50"
                                        }`}
                                >
                                    Delete
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                <AnimatePresence mode="wait">
                                    {manageStatus.type && (
                                        <motion.div
                                            key="manageStatusMsg"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className={`p-4 rounded-xl flex items-start gap-3 mb-6 ${manageStatus.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-red-50 text-red-700 border border-red-100"
                                                }`}
                                        >
                                            {manageStatus.type === "success" ? <CheckCircle size={18} className="shrink-0" /> : <AlertCircle size={18} className="shrink-0" />}
                                            <p className="text-sm font-bold">{manageStatus.message}</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {manageTab === "status" && (
                                    <div className="space-y-4">
                                        <p className="text-sm text-slate-600 mb-4">
                                            Current Status: <strong>{managingUser.status === "disabled" ? "Disabled" : "Active"}</strong>
                                        </p>

                                        {managingUser.status !== "disabled" ? (
                                            <button
                                                onClick={() => handleUpdateStatus(managingUser.id, "disabled")}
                                                disabled={manageLoading}
                                                className="w-full py-4 bg-red-50 text-red-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-100 transition-all border border-red-200 disabled:opacity-50"
                                            >
                                                {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <UserX size={18} />} Deactivate Account
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleUpdateStatus(managingUser.id, "active")}
                                                disabled={manageLoading}
                                                className="w-full py-4 bg-emerald-50 text-emerald-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-100 transition-all border border-emerald-200 disabled:opacity-50"
                                            >
                                                {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <UserCheck size={18} />} Reactivate Account
                                            </button>
                                        )}
                                        <p className="text-xs text-slate-400 mt-2 text-center">
                                            Deactivated users will immediately lose access to the platform.
                                        </p>
                                    </div>
                                )}

                                {manageTab === "permissions" && (
                                    <div className="space-y-4">
                                        <p className="text-sm text-slate-500 mb-4 bg-navy-50 p-3 rounded-lg border border-navy-100">
                                            Control what standard platform tools this specific user can access. Note: The tenant must also have the parent module enabled for these tools to appear.
                                        </p>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                                            {USER_PERMISSIONS_LIST.map((perm) => {
                                                const isEnabled = tempPermissions.includes(perm.id);
                                                return (
                                                    <div
                                                        key={perm.id}
                                                        onClick={() => {
                                                            setTempPermissions((prev) =>
                                                                isEnabled ? prev.filter(p => p !== perm.id) : [...prev, perm.id]
                                                            );
                                                        }}
                                                        className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${isEnabled
                                                            ? 'border-emerald-500 bg-emerald-50/30'
                                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                                            }`}
                                                    >
                                                        <span className={`text-sm font-bold ${isEnabled ? 'text-emerald-900' : 'text-slate-600'}`}>
                                                            {perm.label}
                                                        </span>
                                                        <div>
                                                            {isEnabled ? (
                                                                <ToggleRight size={24} className="text-emerald-500" />
                                                            ) : (
                                                                <ToggleLeft size={24} className="text-slate-300" />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => handleUpdatePermissions(managingUser.id)}
                                            disabled={manageLoading}
                                            className="w-full py-4 bg-navy-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 disabled:opacity-50"
                                        >
                                            {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <Shield size={18} />} Save Permissions
                                        </button>
                                    </div>
                                )}

                                {manageTab === "password" && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">New Password (Min 6 Characters)</label>
                                            <div className="relative">
                                                <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                                <input
                                                    type="password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    placeholder="Enter new password"
                                                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl text-sm outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900 transition-all"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleUpdatePassword(managingUser.id)}
                                            disabled={manageLoading}
                                            className="w-full py-4 bg-navy-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 disabled:opacity-50"
                                        >
                                            {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCw size={18} />} Update Password
                                        </button>
                                    </div>
                                )}

                                {manageTab === "details" && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Full Name</label>
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                                <input
                                                    type="text"
                                                    value={editDetailsData.name}
                                                    onChange={(e) => setEditDetailsData({ ...editDetailsData, name: e.target.value })}
                                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900 transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Company / Tenant</label>
                                            <div className="relative">
                                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                                                <select
                                                    value={editDetailsData.companyId}
                                                    onChange={(e) => setEditDetailsData({ ...editDetailsData, companyId: e.target.value })}
                                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900 transition-all appearance-none bg-white"
                                                >
                                                    <option value="">— No company —</option>
                                                    {companies.map(c => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Address</label>
                                            <div className="relative">
                                                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                                <input
                                                    type="text"
                                                    value={editDetailsData.address}
                                                    onChange={(e) => setEditDetailsData({ ...editDetailsData, address: e.target.value })}
                                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900 transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Payment Plan</label>
                                            <div className="relative">
                                                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                                <input
                                                    type="text"
                                                    value={editDetailsData.paymentPlan}
                                                    onChange={(e) => setEditDetailsData({ ...editDetailsData, paymentPlan: e.target.value })}
                                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900 transition-all"
                                                    placeholder="E.g. Enterprise, Standard, None"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleUpdateDetails(managingUser.id)}
                                            disabled={manageLoading}
                                            className="w-full mt-4 py-3 bg-navy-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 disabled:opacity-50"
                                        >
                                            {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <Edit3 size={18} />} Update User Details
                                        </button>
                                    </div>
                                )}

                                {manageTab === "modules" && (
                                    <div className="space-y-4">
                                        <p className="text-xs text-slate-500 mb-3">Control which sidebar modules this client can access. Core modules (Shipments, Tracking, Messages, Support) are always visible.</p>
                                        {[
                                            { id: "shipments", label: "Shipments & Create Shipment", core: true },
                                            { id: "tracking", label: "Shipment Tracking", core: true },
                                            { id: "messages", label: "Chat / Messages", core: true },
                                            { id: "support", label: "Support Tickets", core: true },
                                            { id: "tools", label: "Tools (Landing & HS Evaluations)" },
                                            { id: "inventory", label: "Inventory & Stock Reconciliation" },
                                            { id: "orders", label: "Order Admin" },
                                            { id: "compliance", label: "Compliance & ATT Accreditation" },
                                            { id: "workflows", label: "Workflow Automation" },
                                            { id: "data", label: "Data & General History" },
                                            { id: "financials", label: "Payable Accounts" },
                                            { id: "api", label: "API Partners" },
                                        ].map((mod) => (
                                            <label key={mod.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${mod.core ? "bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed" : tempModules.includes(mod.id) ? "bg-teal-50 border-teal-300" : "border-slate-200 hover:bg-slate-50"}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={mod.core || tempModules.includes(mod.id)}
                                                    disabled={mod.core}
                                                    onChange={() => {
                                                        if (mod.core) return;
                                                        setTempModules(prev =>
                                                            prev.includes(mod.id)
                                                                ? prev.filter(m => m !== mod.id)
                                                                : [...prev, mod.id]
                                                        );
                                                    }}
                                                    className="rounded accent-teal-600"
                                                />
                                                <span className="text-sm font-medium text-navy-900">{mod.label}</span>
                                                {mod.core && <span className="ml-auto text-[10px] font-bold text-slate-400 uppercase">Core</span>}
                                            </label>
                                        ))}
                                        <button
                                            onClick={() => handleSaveModules(managingUser.id)}
                                            disabled={manageLoading}
                                            className="w-full py-4 bg-navy-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 disabled:opacity-50 mt-4"
                                        >
                                            {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle size={18} />} Save Module Access
                                        </button>
                                    </div>
                                )}

                                {manageTab === "delete" && (
                                    <div className="space-y-4">
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                                            <h4 className="font-bold text-red-700 font-serif flex items-center gap-2 mb-2">
                                                <AlertCircle size={18} /> Danger Zone
                                            </h4>
                                            <p className="text-sm text-red-600 mb-4 font-medium">
                                                Deleting this user will permanently remove their authentication record and their Firestore document. They will completely lose access to TinyGlobal immediately. This action cannot be undone.
                                            </p>
                                            <button
                                                onClick={() => handleDeleteUser(managingUser.id)}
                                                disabled={manageLoading}
                                                className="w-full py-4 bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 disabled:opacity-50"
                                            >
                                                {manageLoading ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={18} />} Permanently Delete User
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
